import { Modal, Spin } from 'antd'
import { useEffect, useState } from 'react'
import DeepZoomViewer from '../components/DeepZoomViewer'
import { platformGetTaskResult, platformTaskMediaUrl } from '../api'
import type { PlatformTaskResult, PlatformTaskResultItem } from '../types'
import { formatDateTime } from './adminShared'
import { platformColors } from './theme'

interface TaskResultModalProps {
  taskId: string | null
  onClose: () => void
}

function MetaRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 12, fontSize: 12, marginBottom: 6 }}>
      <span style={{ width: 88, color: platformColors.secondaryText, flexShrink: 0 }}>{label}</span>
      <span style={{ color: platformColors.primaryText }}>{value}</span>
    </div>
  )
}

function thumbKindFor(item: PlatformTaskResultItem): string {
  if (item.kind === 'transparent' || item.kind === 'background' || item.kind === 'shadow') {
    return `${item.kind}_thumb`
  }
  return item.kind
}

function ResultImageTile({
  taskId,
  item,
  onPreview,
}: {
  taskId: string
  item: PlatformTaskResultItem
  onPreview: (url: string) => void
}) {
  const [broken, setBroken] = useState(false)
  const available = item.available !== false
  const thumb = available ? platformTaskMediaUrl(taskId, thumbKindFor(item)) : null

  if (!available) {
    return (
      <div
        style={{
          border: `1px dashed ${platformColors.border}`,
          borderRadius: 8,
          padding: 8,
          background: '#fafbfc',
          minHeight: 156,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>{item.label}</div>
        <div style={{ fontSize: 12, color: platformColors.mutedText }}>暂无该结果</div>
      </div>
    )
  }

  return (
    <button
      type="button"
      onClick={() => onPreview(platformTaskMediaUrl(taskId, item.kind))}
      style={{
        border: `1px solid ${platformColors.border}`,
        borderRadius: 8,
        padding: 8,
        background: '#fff',
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      {broken ? (
        <div
          style={{
            width: '100%',
            height: 120,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#fef2f2',
            color: '#dc2626',
            borderRadius: 4,
            fontSize: 12,
          }}
        >
          图片加载失败
        </div>
      ) : (
        <img
          src={thumb ?? undefined}
          alt={item.label}
          onError={() => setBroken(true)}
          style={{ width: '100%', height: 120, objectFit: 'contain', background: '#f8fafc', borderRadius: 4 }}
        />
      )}
      <div style={{ marginTop: 8, fontSize: 12, fontWeight: 600 }}>{item.label}</div>
    </button>
  )
}

export default function TaskResultModal({ taskId, onClose }: TaskResultModalProps) {
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<PlatformTaskResult | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [videoBroken, setVideoBroken] = useState(false)
  const [videoDuration, setVideoDuration] = useState<number | null>(null)

  useEffect(() => {
    if (!taskId) {
      setResult(null)
      setPreviewUrl(null)
      setVideoBroken(false)
      setVideoDuration(null)
      return
    }
    setLoading(true)
    setVideoBroken(false)
    setVideoDuration(null)
    platformGetTaskResult(taskId)
      .then((r) => setResult(r.data))
      .catch(() => setResult(null))
      .finally(() => setLoading(false))
  }, [taskId])

  const videoItem = result?.viewer_type === 'video' ? result.items.find((i) => i.available !== false) : null
  const videoUrl = taskId && videoItem ? platformTaskMediaUrl(taskId, videoItem.kind) : null

  return (
    <Modal
      title={taskId ? `查看结果 — ${taskId.slice(0, 8)}` : '查看结果'}
      open={!!taskId}
      onCancel={onClose}
      footer={null}
      width={result?.viewer_type === 'deepzoom' ? 920 : 760}
      destroyOnClose
    >
      {loading && (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin />
        </div>
      )}
      {!loading && result && !result.viewable && (
        <div style={{ color: platformColors.mutedText, padding: 24, textAlign: 'center' }}>暂无可查看结果</div>
      )}
      {!loading && result?.viewable && result.viewer_type === 'video' && videoUrl && (
        <div>
          {videoBroken ? (
            <div
              style={{
                padding: 24,
                textAlign: 'center',
                background: '#fef2f2',
                color: '#dc2626',
                borderRadius: 8,
              }}
            >
              视频加载失败（RESULT_ASSET_RESOLVE_FAILED）
            </div>
          ) : (
            <video
              key={videoUrl}
              src={videoUrl}
              controls
              autoPlay
              playsInline
              onError={() => setVideoBroken(true)}
              onLoadedMetadata={(e) => setVideoDuration(e.currentTarget.duration)}
              style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain', background: '#000', borderRadius: 8 }}
            />
          )}
          <div style={{ marginTop: 16 }}>
            <MetaRow label="任务 ID" value={result.task_id} />
            {result.metadata.width != null && result.metadata.height != null && (
              <MetaRow label="视频尺寸" value={`${result.metadata.width} × ${result.metadata.height}`} />
            )}
            {videoDuration != null && Number.isFinite(videoDuration) && (
              <MetaRow label="播放器时长" value={`${videoDuration.toFixed(2)}s`} />
            )}
            {result.metadata.duration_seconds != null && (
              <MetaRow label="元数据时长" value={`${result.metadata.duration_seconds}s`} />
            )}
            {result.metadata.size_bytes != null && (
              <MetaRow label="文件大小" value={`${Math.round(Number(result.metadata.size_bytes) / 1024)} KB`} />
            )}
            {result.metadata.worker_id != null && <MetaRow label="Worker" value={String(result.metadata.worker_id)} />}
            {result.metadata.finished_at != null && (
              <MetaRow label="完成时间" value={formatDateTime(String(result.metadata.finished_at))} />
            )}
          </div>
        </div>
      )}
      {!loading && result?.viewable && (result.viewer_type === 'image_grid' || result.viewer_type === 'flat_image') && (
        <div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
              gap: 12,
            }}
          >
            {result.items.map((item) => (
              <ResultImageTile
                key={item.kind}
                taskId={result.task_id}
                item={item}
                onPreview={setPreviewUrl}
              />
            ))}
          </div>
          {previewUrl && (
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <img src={previewUrl} alt="preview" style={{ maxWidth: '100%', maxHeight: '60vh', objectFit: 'contain' }} />
            </div>
          )}
        </div>
      )}
      {!loading && result?.viewable && result.viewer_type === 'deepzoom' && result.deepzoom && (
        <DeepZoomViewer
          workspaceId={result.deepzoom.workspace_id}
          assetId={result.deepzoom.asset_id}
          fallbackImageUrl={
            result.deepzoom.fallback_image_url
              ? platformTaskMediaUrl(result.task_id, 'original')
              : null
          }
          height={520}
        />
      )}
    </Modal>
  )
}
